<script lang="ts">
  import { BarChart3, Check, Mail, Share2, Trophy } from "lucide-svelte"
  import type { MarketingUseCase } from "$lib/marketing/use-cases"

  interface Props {
    useCase: MarketingUseCase
    compact?: boolean
  }

  let { useCase, compact = false }: Props = $props()
</script>

<div
  class="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-2xl shadow-slate-900/10"
  data-testid="campaign-flow-preview"
>
  <div class={`bg-gradient-to-br ${useCase.accent} p-6 text-white sm:p-8`}>
    <div class="flex items-start justify-between gap-4">
      <div>
        <p class="text-xs font-bold uppercase tracking-[0.18em] text-white/75">
          Campaign preview
        </p>
        <h3 class="mt-3 text-2xl font-bold tracking-tight">
          {useCase.campaignTitle}
        </h3>
        <p class="mt-2 max-w-md text-sm leading-6 text-white/80">
          Prize idea: {useCase.prizeIdea}
        </p>
      </div>
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 backdrop-blur"
      >
        <Trophy class="h-6 w-6" aria-hidden="true" />
      </div>
    </div>
  </div>

  <div
    class={`grid gap-5 p-5 sm:p-6 ${compact ? "" : "lg:grid-cols-[1fr_0.72fr]"}`}
  >
    <div>
      <div class="mb-4 flex items-center justify-between">
        <h4 class="font-bold text-slate-950">Ways to enter</h4>
        <span
          class="rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-purple-700"
        >
          Mobile ready
        </span>
      </div>
      <div class="space-y-2.5">
        {#each useCase.actions as action, index}
          <div
            class="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3"
          >
            <span
              class="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-purple-600 shadow-sm"
            >
              {#if index === 0}
                <Mail class="h-4 w-4" aria-hidden="true" />
              {:else if index === 1}
                <Check class="h-4 w-4" aria-hidden="true" />
              {:else}
                <Share2 class="h-4 w-4" aria-hidden="true" />
              {/if}
            </span>
            <span class="text-sm font-semibold text-slate-800">{action}</span>
            <span class="ml-auto text-xs font-bold text-purple-600"
              >+{index + 1}</span
            >
          </div>
        {/each}
      </div>
    </div>

    {#if !compact}
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
        <div class="rounded-2xl bg-slate-950 p-5 text-white">
          <BarChart3 class="h-5 w-5 text-purple-300" aria-hidden="true" />
          <p class="mt-5 text-sm font-semibold">Source-aware reporting</p>
          <p class="mt-2 text-xs leading-5 text-slate-400">
            Compare QR, social, email and direct campaign visits without
            presenting illustrative numbers as customer results.
          </p>
        </div>
        <div class="rounded-2xl border border-slate-200 p-5">
          <p
            class="text-xs font-bold uppercase tracking-[0.16em] text-slate-500"
          >
            Next step
          </p>
          <p class="mt-3 text-sm font-semibold leading-6 text-slate-800">
            Draw winners and export consenting entrant data from the same
            campaign workspace.
          </p>
        </div>
      </div>
    {/if}
  </div>
</div>
